import { UsersDropdownInput } from "#src/batteries-included-components/Dropdowns/UsersDropdownInput";
import {
  FilterArea,
  useFilterAreaContentContext,
} from "#src/components/FilterArea";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import {
  DateSelectorInput,
  DropdownInput,
  type StorageKeys,
} from "@validereinc/common-components";
import {
  ActivityAction,
  type AssetActivityLogAssetType,
} from "@validereinc/domain";
import { Assets } from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";
import { z } from "zod";
import { useAssetDropdownInput } from "../assetFilters.helpers";

const { AssetTypeSelection } = Assets.view;

export const AssetActivityLogFilterSchema = z
  .object({
    resource_id: z.string(),
    action: z.string(),
    logged_by: z.string(),
    logged_at: z.object({
      from: z.string().datetime(),
      to: z.string().datetime(),
    }),
  })
  .partial();
export const AssetActivityLogFilterSchemaKeys =
  AssetActivityLogFilterSchema.keyof().Enum;
export type AssetActivityLogFiltersType = z.infer<
  typeof AssetActivityLogFilterSchema
>;

export const AssetActivityLogFilterAreaContent = ({
  assetType,
}: {
  assetType: AssetActivityLogAssetType;
}) => {
  const { handleOnChange } = useFilterAreaContentContext();
  const { localize } = useLocalization();

  const assetSelectionInput = useAssetDropdownInput(
    assetType,
    AssetActivityLogFilterSchemaKeys.resource_id,
    {
      isLabelShown: false,
      isFluid: false,
      isInline: true,
      description: `Filter by ${assetType === AssetTypeSelection.ALL_ASSETS ? "all assets" : `${localize(`${assetType}_plural`)}`}`,
      onChange: (val) =>
        handleOnChange(val, AssetActivityLogFilterSchemaKeys.resource_id),
    }
  );

  const ActivityLogActionsDropdownOptions = [
    {
      value: ActivityAction.CREATE,
      label: toTitleCaseString(ActivityAction.CREATE, {
        intent: "ui",
      }),
    },
    {
      value: ActivityAction.UPDATE,
      label: toTitleCaseString(ActivityAction.UPDATE, {
        intent: "ui",
      }),
    },
    {
      value: ActivityAction.DELETE,
      label: toTitleCaseString(ActivityAction.DELETE, {
        intent: "ui",
      }),
    },
    ...(assetType === AssetTypeSelection.DEVICE
      ? [
          {
            value: ActivityAction.ARCHIVE,
            label: toTitleCaseString(ActivityAction.ARCHIVE, {
              intent: "ui",
            }),
          },
        ]
      : []),
  ];

  return (
    <>
      <DropdownInput
        style={{ marginBottom: 0 }}
        label="Change Type"
        name={AssetActivityLogFilterSchemaKeys.action}
        labelKey="label"
        valueKey="value"
        placeholder="Select Change Type..."
        description="Filter by Change Type"
        isInline
        isRequired
        isLabelShown={false}
        options={ActivityLogActionsDropdownOptions}
        onChange={(val: string) =>
          handleOnChange(val, AssetActivityLogFilterSchemaKeys.action)
        }
        noSearchDebounce
      />
      {assetSelectionInput}
      <UsersDropdownInput
        style={{ marginBottom: 0 }}
        name={AssetActivityLogFilterSchemaKeys.logged_by}
        label="Logged By"
        placeholder="Select Logged By..."
        description="Filter by Logged By"
        isInline
        isOptionalTextShown={false}
        isLabelShown={false}
        onChange={(val: string) =>
          handleOnChange(val, AssetActivityLogFilterSchemaKeys.logged_by)
        }
      />
      <DateSelectorInput
        name={AssetActivityLogFilterSchemaKeys.logged_at}
        variant="day"
        label="Logged At"
        description="Filter by Logged At"
        isLabelShown={false}
        isRange
        isInline
        onChange={(val) =>
          handleOnChange(val, AssetActivityLogFilterSchemaKeys.logged_at)
        }
      />
    </>
  );
};

export const AssetActivityLogFilterArea = ({
  viewConfigStorageKey,
  assetType,
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  assetType: AssetActivityLogAssetType;
}) => {
  return (
    <FilterArea.Root
      storageKey={viewConfigStorageKey}
      defaultValues={{
        logged_at: DEFAULT_DATE_RANGES.lastThreeMonths,
        resource_id: "",
      }}
      applyDefaultValues
      shouldPrioritizeStoredFiltersWhenApplyingDefaultValues
    >
      <FilterArea.Container style={{ marginBottom: 16 }}>
        <FilterArea.Content>
          {() => (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              <AssetActivityLogFilterAreaContent assetType={assetType} />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
